import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import gql from "graphql-tag"
import TaskManagementListHeader from './TaskManagementListHeader'
import TaskList from './TaskList'
import MyTasksGroupedList from './MyTasksGroupedList'
import TaskKanbanView from './TaskKanbanView'
import withApolloClient from '../withApolloClient'
import { UserQuery } from '../Functions/querys'

const UpdateMemberMutation = gql`
    mutation updateMember($input: UpdateMemberMutationInput!) {
        updateMember(input: $input) {
            ok
            errors {
                messages
                field
            }
        }
    }
`

// First-time defaults per board type.
const DEFAULT_CUSTOM_BOARD_FILTERS = { searchTerm: '', isArchived: false }
const DEFAULT_MY_TASK_FILTERS = { searchTerm: '', isArchived: false, statuses: ["OPEN", "IN_PROGRESS"] }

function TaskManagementViewContainer({ apolloClient, defaultColumnConfig, tablePreferenceId, currentUser }) {
    const { boardId } = useParams()
    const activeTab = !boardId || boardId === 'tasks' ? "myTasks" : "boardTasks"
    const { data: userData, loading: userPrefsLoading } = useQuery(UserQuery, { fetchPolicy: 'cache-first' })
    const memberPrefs = userData?.currentUser?.member
    const memberPrefsRef = useRef(memberPrefs)
    const [columnConfig, setColumnConfig] = useState(defaultColumnConfig || [])
    const lastTablePreferenceIdRef = useRef(null)

    const [orderBy, setOrderBy] = useState(['dueDate'])
    const [groupBy, setGroupBy] = useState('status')
    const [pendingViewType, setPendingViewType] = useState(null)
    const [filterValues, setFilterValues] = useState({ searchTerm: '', isArchived: false })
    const [filterHydrated, setFilterHydrated] = useState(false)
    const filterHydratedRef = useRef(false)

    const getPersistedViewType = () => {
        try {
            const config = JSON.parse(memberPrefs?.boardViewTypeConfig || '{}')
            const boardKey = boardId || "tasks"
            return config[boardKey] || memberPrefs?.taskManagementViewType || "list"
        } catch (e) {
            return memberPrefs?.taskManagementViewType || "list"
        }
    }

    const viewType = pendingViewType !== null ? pendingViewType : getPersistedViewType()

    useEffect(() => { memberPrefsRef.current = memberPrefs }, [memberPrefs])

    useEffect(() => {
        setPendingViewType(null)
        // Reset hydration so the new board's saved filters are loaded.
        filterHydratedRef.current = false
        setFilterHydrated(false)
    }, [boardId])

    useEffect(() => {
        if (!memberPrefs?.kanbanColumnConfig) return
        try {
            const config = JSON.parse(memberPrefs.kanbanColumnConfig)
            setGroupBy(boardId && config[boardId] ? config[boardId] : 'status')
        } catch (e) { }
    }, [memberPrefs?.kanbanColumnConfig, boardId])

    // Hydrate filter values from saved preferences when memberPrefs loads or board changes.
    // Always sets filterHydrated so the task list only renders with the correct filters.
    useEffect(() => {
        if (!memberPrefs || filterHydratedRef.current) return
        filterHydratedRef.current = true
        const boardKey = boardId || "tasks"
        const defaults = boardId ? DEFAULT_CUSTOM_BOARD_FILTERS : DEFAULT_MY_TASK_FILTERS
        try {
            const config = JSON.parse(memberPrefs.taskFilterConfig || '{}')
            const saved = config[boardKey]
            if (saved && typeof saved === 'object') {
                setFilterValues({ ...defaults, ...saved })
            } else {
                setFilterValues(defaults)
            }
        } catch (e) {
            setFilterValues(defaults)
        }
        setFilterHydrated(true)
    }, [memberPrefs, boardId])

    useEffect(() => {
        if (lastTablePreferenceIdRef.current === tablePreferenceId) return
        lastTablePreferenceIdRef.current = tablePreferenceId
        setColumnConfig(defaultColumnConfig || [])
    }, [defaultColumnConfig, tablePreferenceId])

    // Keep saved filters intact and only strip statuses from the live query when
    // the kanban columns are already grouped by status.
    const effectiveFilterValues = useMemo(() => {
        if (viewType !== "kanban" || groupBy !== "status" || !filterValues?.statuses) {
            return filterValues
        }
        const { statuses, ...remainingFilters } = filterValues
        return remainingFilters
    }, [filterValues, groupBy, viewType])

    const handleGroupByChange = async (newGroupBy) => {
        setGroupBy(newGroupBy)
        if (!boardId) return
        try {
            let existing = {}
            if (memberPrefs?.kanbanColumnConfig) {
                try { existing = JSON.parse(memberPrefs.kanbanColumnConfig) } catch (e) { }
            }
            await apolloClient.mutate({
                mutation: UpdateMemberMutation,
                variables: { input: { memberId: memberPrefs?.id, kanbanColumnConfig: JSON.stringify({ ...existing, [boardId]: newGroupBy }) } },
                refetchQueries: ['UserQuery'],
            })
        } catch (e) { }
    }

    const handleViewTypeChange = async (newViewType) => {
        setPendingViewType(newViewType)
        try {
            let existing = {}
            if (memberPrefs?.boardViewTypeConfig) {
                try { existing = JSON.parse(memberPrefs.boardViewTypeConfig) } catch (e) { }
            }
            const boardKey = boardId || "tasks"
            await apolloClient.mutate({
                mutation: UpdateMemberMutation,
                variables: {
                    input: {
                        memberId: memberPrefs?.id,
                        boardViewTypeConfig: JSON.stringify({ ...existing, [boardKey]: newViewType }),
                    }
                },
                refetchQueries: ["UserQuery"],
            })
        } catch (error) {
            console.error("Error saving view type:", error)
        }
    }

    // Persist durable filters per board. searchTerm is always saved as empty — it's transient.
    const saveFilterPreference = useCallback(async (newFilterValues) => {
        const prefs = memberPrefsRef.current
        if (!prefs?.id) return
        const boardKey = boardId || "tasks"
        const defaults = boardId ? DEFAULT_CUSTOM_BOARD_FILTERS : DEFAULT_MY_TASK_FILTERS
        const filtersToSave = { ...newFilterValues, searchTerm: '' }
        try {
            let existing = {}
            if (prefs.taskFilterConfig) {
                try { existing = JSON.parse(prefs.taskFilterConfig) } catch (e) { }
            }
            const previousBoardFilters = existing[boardKey] || {}
            // Skip write when nothing changed.
            if (JSON.stringify(previousBoardFilters) === JSON.stringify(filtersToSave)) return

            const nextConfig = { ...existing }
            // If back to defaults, remove the saved override for this board.
            if (JSON.stringify(filtersToSave) === JSON.stringify(defaults)) {
                delete nextConfig[boardKey]
            } else {
                nextConfig[boardKey] = filtersToSave
            }
            await apolloClient.mutate({
                mutation: UpdateMemberMutation,
                variables: {
                    input: {
                        memberId: prefs.id,
                        taskFilterConfig: JSON.stringify(nextConfig),
                    }
                },
                refetchQueries: ['UserQuery'],
            })
        } catch (e) {
            console.error("Error saving filter preference:", e)
        }
    }, [apolloClient, boardId])

    // Single setter that keeps local state and saved preferences in sync.
    const handleSetFilterValues = useCallback((newFilterValues) => {
        setFilterValues(newFilterValues)
        saveFilterPreference(newFilterValues)
    }, [saveFilterPreference])

    const updateOrderBy = (string) => {
        let order = [...orderBy]
        if (!order.includes(string) && !order.includes("-" + string)) {
            order = [string]
        } else {
            let index = order.indexOf(string)
            if (order.indexOf(string) > -1) {
                order[index] = "-" + string
            } else {
                index = order.indexOf("-" + string)
                index > -1 && (order[index] = string)
            }
        }
        setOrderBy(order)
    }

    return (
        <>
            <TaskManagementListHeader
                activeTab={activeTab}
                filterValues={filterValues}
                setFilterValues={handleSetFilterValues}
                showNewBoardButton
                viewType={viewType}
                onViewTypeChange={handleViewTypeChange}
                groupBy={groupBy}
                onGroupByChange={handleGroupByChange}
                tableId={tablePreferenceId}
                defaultColumnConfig={defaultColumnConfig}
                columnConfig={columnConfig}
                setColumnConfig={setColumnConfig}
                currentUser={currentUser}
            />

            {(userPrefsLoading && !memberPrefs) || !filterHydrated ? (
                <div className="text-center mt-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
            ) : viewType === "kanban" ? (
                boardId ? (
                    <TaskKanbanView boardId={boardId} {...effectiveFilterValues} orderBy={orderBy} groupBy={groupBy} />
                ) : (
                    <TaskKanbanView assignedToIds={[currentUser.id]} {...effectiveFilterValues} orderBy={orderBy} groupBy={groupBy} />
                )
            ) : (
                boardId ? (
                    <TaskList boardId={boardId} {...effectiveFilterValues} orderBy={orderBy} updateOrderBy={updateOrderBy} columnConfig={columnConfig} />
                ) : (
                    <MyTasksGroupedList currentUserId={currentUser.id} filterValues={filterValues} orderBy={orderBy} updateOrderBy={updateOrderBy} columnConfig={columnConfig} />
                )
            )}
        </>
    )
}

export default withApolloClient(TaskManagementViewContainer)
